import { useCallback, useEffect, useState } from 'react';
import { Badge, Empty, Field, Note, Panel, ShieldIcon, Spinner, TableWrap } from '@wyocspay/ui';
import { ApiError, api, type PermissionDefinition, type RoleDefinition, type StaffMember } from './api.js';

/**
 * Roles and permissions.
 *
 * Role names and permission keys stay in American English by requirement: they
 * are identifiers that appear in the audit log and over the API, and an
 * identifier space that is half Bulgarian is worse than one that is entirely
 * foreign. The surrounding console text is Bulgarian, as everywhere else.
 */

const ROLE_TONE: Record<string, 'fail' | 'warn' | 'info' | 'neutral'> = {
  SUPER_ADMIN: 'fail',
  ADMIN: 'warn',
  OPERATOR: 'info',
  SUPPORT: 'neutral',
  ANALYST: 'neutral',
};

export function AccessView({ currentUserId }: { currentUserId: string }) {
  const [staff, setStaff] = useState<StaffMember[] | null>(null);
  const [catalogue, setCatalogue] = useState<{
    roles: RoleDefinition[];
    permissions: PermissionDefinition[];
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<{ tone: 'ok' | 'warn'; text: string } | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [s, c] = await Promise.all([api.staff(), api.accessCatalogue()]);
      setStaff(s.data);
      setCatalogue(c);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  if (error) return <Note tone="danger">{error}</Note>;
  if (!staff || !catalogue) return <Spinner label="Зареждане…" />;

  const me = staff.find((u) => u.id === currentUserId);
  const iAmSuper = me?.role === 'SUPER_ADMIN';

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <Note title="Само главният администратор раздава права">
        Ролята е долна граница, не ограда - индивидуалните права се <em>добавят</em> към нея и
        никога не отнемат. Затова „какво може този човек" е един списък, а не два, които трябва да
        се съгласуват в правилния ред.
      </Note>

      {msg ? <Note tone={msg.tone}>{msg.text}</Note> : null}

      {created ? (
        <Note tone="warn" title="Паролата се показва само сега">
          <div style={{ fontSize: 13.5, marginBottom: 8 }}>
            Профил <strong>{created.email}</strong>. Запишете паролата - не се пази в четим вид и
            няма как да бъде показана отново. Профилът все още не може да влезе, докато не се
            регистрира устройство за него.
          </div>
          <pre
            className="wc-num"
            style={{ margin: 0, fontSize: 15, userSelect: 'all', wordBreak: 'break-all' }}
          >
            {created.password}
          </pre>
          <button
            type="button"
            className="wc-btn wc-btn-sm wc-btn-secondary"
            style={{ marginTop: 10 }}
            onClick={() => setCreated(null)}
          >
            Записах я
          </button>
        </Note>
      ) : null}

      {iAmSuper ? (
        <div>
          <button type="button" className="wc-btn" onClick={() => setCreating((v) => !v)}>
            {creating ? 'Откажи' : 'Нов служебен профил'}
          </button>
        </div>
      ) : null}

      {creating && iAmSuper ? (
        <CreateStaff
          catalogue={catalogue}
          onCancel={() => setCreating(false)}
          onCreated={(result) => {
            setCreating(false);
            setCreated(result);
            void load();
          }}
        />
      ) : null}

      <Panel title="Служебни профили" padded={false}>
        {staff.length === 0 ? (
          <Empty>Няма служебни профили.</Empty>
        ) : (
          <TableWrap>
            <table className="wc-table">
              <thead>
                <tr>
                  <th>Име</th>
                  <th>Роля</th>
                  <th>Права</th>
                  <th>Устройства</th>
                  <th>Статус</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {staff.map((u) => (
                  <tr key={u.id}>
                    <td>
                      <strong style={{ fontSize: 14 }}>
                        {u.name}
                        {u.id === currentUserId ? (
                          <span style={{ color: 'var(--wc-faint)', fontWeight: 400 }}> (вие)</span>
                        ) : null}
                      </strong>
                      <div style={{ fontSize: 12.5, color: 'var(--wc-muted)' }}>{u.email}</div>
                    </td>
                    <td>
                      <Badge tone={ROLE_TONE[u.role] ?? 'neutral'} icon={false}>
                        {u.role}
                      </Badge>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      {u.effective_count} общо
                      {u.extra_permissions.length > 0 ? (
                        <div style={{ fontSize: 12, color: 'var(--wc-muted)' }}>
                          +{u.extra_permissions.length} допълнителни
                        </div>
                      ) : null}
                      {u.can_bypass_approval ? (
                        <div style={{ marginTop: 3 }}>
                          <Badge tone="warn" icon={false}>
                            ПОДПИСВА САМ
                          </Badge>
                        </div>
                      ) : null}
                    </td>
                    <td className="wc-num">{u.devices}</td>
                    <td>
                      <Badge tone={u.status === 'ACTIVE' ? 'ok' : 'fail'}>
                        {u.status === 'ACTIVE' ? 'АКТИВЕН' : 'ЗАМРАЗЕН'}
                      </Badge>
                    </td>
                    <td className="wc-right">
                      <button
                        type="button"
                        className="wc-btn wc-btn-sm wc-btn-secondary"
                        onClick={() => setEditing(editing === u.id ? null : u.id)}
                      >
                        {editing === u.id ? 'Затвори' : 'Права'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </Panel>

      {editing ? (
        <EditAccess
          user={staff.find((u) => u.id === editing)!}
          catalogue={catalogue}
          canEdit={iAmSuper}
          isSelf={editing === currentUserId}
          onSaved={(text) => {
            setMsg({ tone: 'ok', text });
            void load();
          }}
          onError={(text) => setMsg({ tone: 'warn', text })}
        />
      ) : null}

      <Panel title="Какво може всяка роля">
        <div style={{ display: 'grid', gap: 14 }}>
          {catalogue.roles.map((r) => (
            <div key={r.key}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'baseline' }}>
                <Badge tone={ROLE_TONE[r.key] ?? 'neutral'} icon={false}>
                  {r.key}
                </Badge>
                <strong style={{ fontSize: 14 }}>{r.label}</strong>
                <span style={{ fontSize: 12.5, color: 'var(--wc-faint)' }}>
                  {r.implicit ? 'всички права' : `${r.permissions.length} права`}
                </span>
              </div>
              <p style={{ margin: '3px 0 0', fontSize: 13, color: 'var(--wc-muted)' }}>
                {r.description}
              </p>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------

function PermissionGrid({
  permissions,
  selected,
  covered,
  disabled,
  onToggle,
}: {
  permissions: PermissionDefinition[];
  selected: Set<string>;
  /** Already granted by the role, so ticking them adds nothing. */
  covered: Set<string>;
  disabled: boolean;
  onToggle: (key: string) => void;
}) {
  const groups = [...new Set(permissions.map((p) => p.group))];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {groups.map((group) => (
        <div key={group}>
          <div
            style={{
              fontSize: 10.5,
              fontWeight: 700,
              letterSpacing: '0.09em',
              textTransform: 'uppercase',
              color: 'var(--wc-faint)',
              marginBottom: 6,
            }}
          >
            {group}
          </div>
          <div style={{ display: 'grid', gap: 6 }}>
            {permissions
              .filter((p) => p.group === group)
              .map((p) => {
                const byRole = covered.has(p.key);
                return (
                  <label
                    key={p.key}
                    style={{
                      display: 'flex',
                      gap: 9,
                      alignItems: 'start',
                      fontSize: 13.5,
                      opacity: byRole ? 0.6 : 1,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={byRole || selected.has(p.key)}
                      disabled={disabled || byRole}
                      onChange={() => onToggle(p.key)}
                      style={{ marginTop: 3 }}
                    />
                    <span>
                      <span style={{ fontWeight: 600 }}>{p.label}</span>
                      {p.sensitive ? (
                        <Badge tone="warn" icon={false}>
                          SENSITIVE
                        </Badge>
                      ) : null}
                      {byRole ? (
                        <span style={{ color: 'var(--wc-faint)', fontSize: 12 }}> · от ролята</span>
                      ) : null}
                      <div style={{ color: 'var(--wc-muted)', fontSize: 12.5 }}>{p.description}</div>
                      <code style={{ fontSize: 11, color: 'var(--wc-faint)' }}>{p.key}</code>
                    </span>
                  </label>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
}

function EditAccess({
  user,
  catalogue,
  canEdit,
  isSelf,
  onSaved,
  onError,
}: {
  user: StaffMember;
  catalogue: { roles: RoleDefinition[]; permissions: PermissionDefinition[] };
  canEdit: boolean;
  isSelf: boolean;
  onSaved: (text: string) => void;
  onError: (text: string) => void;
}) {
  const [role, setRole] = useState(user.role);
  const [extra, setExtra] = useState(new Set(user.extra_permissions));
  const [busy, setBusy] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  const roleDef = catalogue.roles.find((r) => r.key === role);
  const covered = new Set(roleDef?.implicit ? catalogue.permissions.map((p) => p.key) : (roleDef?.permissions ?? []));

  async function save(): Promise<void> {
    setBusy(true);
    try {
      if (role !== user.role) await api.setStaffRole(user.id, role);
      await api.setStaffPermissions(user.id, [...extra]);
      onSaved(`Правата на ${user.name} са записани.`);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function changePassword(): Promise<void> {
    setBusy(true);
    try {
      const result = await api.setStaffPassword(user.id, newPassword);
      setNewPassword('');
      onSaved(
        `Паролата е сменена. Прекратени сесии: ${result.sessions_terminated}.` +
          (isSelf ? ' Ще трябва да влезете наново.' : ''),
      );
    } catch (err) {
      onError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title={`Достъп: ${user.name}`}>
      {!canEdit ? (
        <Note tone="warn">
          Само главен администратор може да променя роли и права. Показаното е само за преглед.
        </Note>
      ) : null}

      <Field label="Роля">
        <select
          className="wc-input"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          disabled={!canEdit || busy}
        >
          {catalogue.roles.map((r) => (
            <option key={r.key} value={r.key}>
              {r.key} - {r.label}
            </option>
          ))}
        </select>
      </Field>

      <div style={{ margin: '4px 0 16px', fontSize: 13, color: 'var(--wc-muted)' }}>
        {roleDef?.description}
      </div>

      <div
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '0.09em',
          textTransform: 'uppercase',
          color: 'var(--wc-faint)',
          marginBottom: 10,
        }}
      >
        Допълнителни права над ролята
      </div>

      <PermissionGrid
        permissions={catalogue.permissions}
        selected={extra}
        covered={covered}
        disabled={!canEdit || busy}
        onToggle={(key) =>
          setExtra((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
          })
        }
      />

      {canEdit ? (
        <button type="button" className="wc-btn" onClick={save} disabled={busy} style={{ marginTop: 16 }}>
          <ShieldIcon size={13} /> {busy ? 'Записване…' : 'Запази ролята и правата'}
        </button>
      ) : null}

      {canEdit || isSelf ? (
        <div style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid var(--wc-border)' }}>
          <Field
            label={isSelf ? 'Нова парола за вашия профил' : `Нова парола за ${user.name}`}
            hint="Поне 12 знака. Смяната прекратява всички активни сесии на профила."
          >
            <input
              className="wc-input"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <button
            type="button"
            className="wc-btn wc-btn-secondary"
            onClick={changePassword}
            disabled={busy || newPassword.length < 12}
          >
            Смени паролата
          </button>
        </div>
      ) : null}
    </Panel>
  );
}

function CreateStaff({
  catalogue,
  onCancel,
  onCreated,
}: {
  catalogue: { roles: RoleDefinition[]; permissions: PermissionDefinition[] };
  onCancel: () => void;
  onCreated: (result: { email: string; password: string }) => void;
}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('OPERATOR');
  const [extra, setExtra] = useState(new Set<string>());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const roleDef = catalogue.roles.find((r) => r.key === role);
  const covered = new Set(roleDef?.implicit ? catalogue.permissions.map((p) => p.key) : (roleDef?.permissions ?? []));

  async function submit(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      const created = await api.createStaff({ name, email, role, permissions: [...extra] });
      onCreated({ email: created.email, password: created.password });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="Нов служебен профил">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Име">
          <input className="wc-input" value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Имейл">
          <input
            className="wc-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
      </div>

      <Field label="Роля">
        <select className="wc-input" value={role} onChange={(e) => setRole(e.target.value)}>
          {catalogue.roles.map((r) => (
            <option key={r.key} value={r.key}>
              {r.key} - {r.label}
            </option>
          ))}
        </select>
      </Field>

      <div style={{ margin: '4px 0 16px', fontSize: 13, color: 'var(--wc-muted)' }}>
        {roleDef?.description}
      </div>

      <PermissionGrid
        permissions={catalogue.permissions}
        selected={extra}
        covered={covered}
        disabled={busy}
        onToggle={(key) =>
          setExtra((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
          })
        }
      />

      {error ? <div style={{ marginTop: 12 }}><Note tone="danger">{error}</Note></div> : null}

      <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
        <button
          type="button"
          className="wc-btn"
          onClick={submit}
          disabled={busy || name.trim().length < 2 || !email.includes('@')}
        >
          {busy ? 'Създаване…' : 'Създай профила'}
        </button>
        <button type="button" className="wc-btn wc-btn-secondary" onClick={onCancel} disabled={busy}>
          Откажи
        </button>
      </div>
    </Panel>
  );
}
